import { DatePicker, message } from 'antd'
import type { ColumnType } from 'antd/es/table/interface'
import dayjs from 'dayjs'
import React, { useCallback } from 'react'

import { datePickerCsLocale } from '../components/PriceEstimateBaseFragment/PriceEstimateBaseFragment'
import { TableFilterDropdownContainer } from '../components/TableFilterDropdownContainer/TableFilterDropdownContainer'
import { czDateFormatDatePickerDay } from './dateFormat'
import { columnFilterIcon } from './useColumnFilters'

const datePickerProps = {
  allowClear: true,
  locale: datePickerCsLocale,
  className: '!block w-full',
  format: {
    format: czDateFormatDatePickerDay,
    type: 'mask' as const,
  },
}
/**
 * client-side Ant Table columns filter by Date range.
 */
export function useDateRangeFilter<T>() {
  const getDateRangeFilter = useCallback(
    (dataIndex: keyof T, placeholder?: [string, string]): ColumnType<T> => ({
      ...columnFilterIcon,
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
        close,
      }) => {
        let startDate: dayjs.Dayjs | null = null
        let endDate: dayjs.Dayjs | null = null

        if (selectedKeys.length > 0 && typeof selectedKeys[0] === 'string') {
          try {
            const [start, end] = JSON.parse(selectedKeys[0])
            if (start) {
              startDate = dayjs(start)
            }
            if (end) {
              endDate = dayjs(end)
            }
          } catch (error) {
            message.error('Chyba při parsování datového rozsahu')
          }
        }

        const updateSelectedKeys = (
          newStartDate: dayjs.Dayjs | null,
          newEndDate: dayjs.Dayjs | null
        ) => {
          const dateRange = [
            newStartDate ? newStartDate.startOf('day').format() : null,
            newEndDate ? newEndDate.endOf('day').format() : null,
          ]

          if (dateRange[0] === null && dateRange[1] === null) {
            setSelectedKeys([])
          } else {
            setSelectedKeys([JSON.stringify(dateRange)])
          }
        }

        return (
          <TableFilterDropdownContainer
            clearFilters={clearFilters}
            confirm={confirm}
            close={close}
            isSelected={!!selectedKeys?.length}
          >
            <div className="space-y-2">
              <div>
                <DatePicker
                  placeholder={placeholder ? placeholder[0] : 'Od (nepovinné)'}
                  value={startDate}
                  onChange={date => {
                    updateSelectedKeys(date, endDate)
                  }}
                  {...datePickerProps}
                />
              </div>
              <div>
                <DatePicker
                  placeholder={placeholder ? placeholder[1] : 'Do (nepovinné)'}
                  value={endDate}
                  onChange={date => {
                    updateSelectedKeys(startDate, date)
                  }}
                  {...datePickerProps}
                />
              </div>
            </div>
          </TableFilterDropdownContainer>
        )
      },
      onFilter: (value, record) => {
        if (!value || typeof value !== 'string' || !record[dataIndex]) {
          return false
        }

        try {
          const [startDate, endDate] = JSON.parse(value)
          const recordDate = dayjs(record[dataIndex] as string)

          if (!startDate && !endDate) {
            return false
          }

          if (startDate && !endDate) {
            const start = dayjs(startDate)
            return (
              recordDate.isAfter(start) ||
              (recordDate.year() === start.year() &&
                recordDate.month() === start.month() &&
                recordDate.date() === start.date())
            )
          }

          if (!startDate && endDate) {
            const end = dayjs(endDate)
            return (
              recordDate.isBefore(end) ||
              (recordDate.year() === end.year() &&
                recordDate.month() === end.month() &&
                recordDate.date() === end.date())
            )
          }

          const start = dayjs(startDate)
          const end = dayjs(endDate)

          return (
            (recordDate.isAfter(start) ||
              (recordDate.year() === start.year() &&
                recordDate.month() === start.month() &&
                recordDate.date() === start.date())) &&
            (recordDate.isBefore(end) ||
              (recordDate.year() === end.year() &&
                recordDate.month() === end.month() &&
                recordDate.date() === end.date()))
          )
        } catch (error) {
          message.error('Chyba při filtrování podle datového rozsahu')
          return false
        }
      },
    }),
    []
  )

  return { getDateRangeFilter }
}
